// R5.3 — THE USER-EDIT BOUNDARY, AS A PURE MODULE SHARED BY BOTH SIDES OF THE WIRE (spec §1.5, codebook §2.1).
//
// EXTRACTED FROM `api/factLedger.ts`, NOT REWRITTEN. Every value below is the byte-identical constant, validator,
// or function the R5.1 PATCH handler already ran; `api/factLedger.ts` now imports them from here instead of
// declaring them, so there is exactly one definition of "what a user may edit" and "what counts as inert".
//
// WHY IT IS SHARED. §1.5's client half requires that "user edit inputs [are] NFC-normalized client-side too,
// length-limited, rendered only as text nodes". A client that re-implemented the normalization would be a second
// opinion about the same rule, and the two would drift the first time either changed — the exact failure mode
// round-2 finding 3 closed for the stored-shape validator by extracting `rubricReadShape.ts`. This is that same
// move, for the same reason: the client normalizes and bounds EXACTLY what the server normalizes and bounds,
// by construction rather than by synchronization.
//
// THE CLIENT DOES NOT THEREBY BECOME THE AUTHORITY. Client-side normalization exists so the applicant is told
// "this is too long" before a round trip, and so the value they see in the field is the value that will be
// stored. The server re-runs all of it on arrival and refuses independently (`413 value_too_long`,
// `400 invalid_characters`, `400 invalid_edit_value`); nothing here is trusted because a browser ran it.
//
// PURE BY CONSTRUCTION — no D1, no Hono, no environment, no I/O. Same contract as `rubricCopy.ts` and
// `rubricReadShape.ts`, which the client already imports directly. It imports exactly one thing, and only from an
// equally pure sibling: `STORABLE_ENTRY_TYPES`, the set the database will actually accept. That import is the
// point — this file previously carried its OWN copy of the entry_type list, the copy drifted ahead of the schema
// on 2026-08-23, and an applicant correcting an entry's type to a value D1 refuses would have taken a 500.
import { STORABLE_ENTRY_TYPES } from './factLedger';

/* ================================================================================================================
 * §2.1 rule 5 — THE EDITABLE FIELD SET
 *
 * "The user can edit ANY fact." The editable set is therefore every §2.2 field the ledger stores a user-correctable
 * value for, with a validator each — a user edit is DATA (§11.3), which is exactly why it gets schema-checked like
 * any other data rather than trusted because a human typed it.
 * ============================================================================================================== */

export type EditValidator = (v: unknown) => boolean;

const isNotStated = (v: unknown) => v === 'not_stated';
const str: EditValidator = (v) => typeof v === 'string' && v.length > 0;
const strOrNs: EditValidator = (v) => isNotStated(v) || str(v);
const oneOf = (...vals: string[]): EditValidator => (v) => isNotStated(v) || (typeof v === 'string' && vals.includes(v));
const datePartial: EditValidator = (v) => isNotStated(v) || (typeof v === 'string' && /^\d{4}(-(0[1-9]|1[0-2]))?$/.test(v));

export const EDITABLE_FIELDS: ReadonlyMap<string, EditValidator> = new Map<string, EditValidator>([
  ['role_title', strOrNs],
  ['responsibility_statement', strOrNs],
  ['duration_stated', strOrNs],
  ['institution', strOrNs],
  ['jurisdiction', strOrNs],
  ['organization_context_id', strOrNs],
  ['date_start', datePartial],
  ['date_end', datePartial],
  ['is_ongoing', (v) => isNotStated(v) || typeof v === 'boolean'],
  ['team_size', (v) => isNotStated(v) || (typeof v === 'number' && Number.isInteger(v) && v >= 0)],
  ['frequency', oneOf('one_off', 'weekly', 'monthly', 'per_term', 'irregular', 'continuous')],
  ['supervision_level', oneOf('observer_shadowing', 'supervised_student', 'supervised_trainee_postgrad', 'stated_independent', 'non_clinical')],
  ['training_stage_at_time', oneOf('pre_med', 'undergrad_med', 'postgrad', 'post_licensure', 'non_medical')],
  ['activity_status', oneOf('completed', 'under_way')],
  // entry_type has NO not_stated member (§2.2) — `other` is its catch-all.
  // Reads the SCHEMA GATE, never the `EntryType` union: a correction is a WRITE, and accepting a value D1 will
  // refuse turns an applicant's edit into a 500. See `STORABLE_ENTRY_TYPES`.
  ['entry_type', (v) => typeof v === 'string' && (STORABLE_ENTRY_TYPES as readonly string[]).includes(v)],
]);

/**
 * THE FIELDS THE R5.3 LEDGER UI OFFERS AN EDITOR FOR — now **every** member of `EDITABLE_FIELDS` but one.
 *
 * WHAT CHANGED, AND WHY (R5.3 round 2, build-review finding 4). Round 1 offered controls for five free-text
 * fields and named the reason honestly: Appendix A registered no field labels and no enum-value labels, so an
 * enum field had no display text for any of its options and a date/count field had no label at all. The review
 * REJECTED that as a release resolution — "the copy constraint requires registering missing copy; it does not
 * permit omitting required typed values or edit controls" — and codebook §2.1 rule 5 is unambiguous: **the user
 * can edit any fact.** The missing copy is now registered (Appendix A.5-P, `rubricCopy.ts`) and resolved through
 * `factLabels.ts`, so the constraint that bounded this set is gone and the set is bounded only by what the PATCH
 * route accepts.
 *
 * THE ONE REMAINING EXCLUSION IS NOT A COPY GAP. `organization_context_id` is an OPAQUE distinctness token
 * (§2.2/§4.2/§11.2): an anonymous id whose only permitted use is equality comparison for the breadth rule, which
 * carries no name, no geography, no tier and no ordering, and whose labels are explicitly free to renumber
 * (T-INV-2). It is not display text and it is not the applicant's prose; offering it as an editable value would
 * invite an edit that silently re-partitions the entry's organization grouping while showing the applicant an
 * internal token. It renders no value and offers no editor, and `factLabels.ts` says so by giving it the
 * `opaque` KIND rather than by leaving it out of a table.
 *
 * `factLabels.test.ts` asserts this set is exactly `EDITABLE_FIELDS` minus `UI_UNSUPPORTED_EDIT_FIELDS`, that
 * every excluded field is accounted for by name, and that every enum option the surface offers is one the
 * server's own validator accepts — so adding a field to one side without deciding about the other fails.
 */
export const UI_UNSUPPORTED_EDIT_FIELDS: Readonly<Record<string, string>> = Object.freeze({
  organization_context_id: 'opaque distinctness token (§2.2/§4.2/§11.2) — not display text, and an edit would silently re-partition the breadth rule',
});

/** Every field the surface offers an editor for: the API's set, minus the one opaque token above. */
export const UI_EDITABLE_FIELDS: readonly string[] =
  [...EDITABLE_FIELDS.keys()].filter((f) => !(f in UI_UNSUPPORTED_EDIT_FIELDS));

/**
 * THE R5.2/R5.3-round-1 NAME, kept as an alias so no call site breaks — and deliberately NOT kept as a different
 * set. There is one answer to "which fields may the surface edit", and two exported names for it would be two
 * answers the moment either moved.
 */
export const UI_TEXT_EDIT_FIELDS: readonly string[] = UI_EDITABLE_FIELDS;

/** Columns whose stored form differs from the §2 value the user supplies. */
export function storageValue(field: string, value: unknown): unknown {
  if (value === 'not_stated') return null;
  if (field === 'is_ongoing') return value === true ? 1 : 0;
  return value;
}

/* ================================================================================================================
 * §1.5 — THE INERT-INPUT BOUNDARY
 *
 * User-supplied strings enter rendered output by design; the boundary is SPECIFIED, not assumed. Normalizing and
 * bounding at ingestion — before storage and before hashing — is what makes the client's inert-rendering rule
 * meaningful: a value that was never normalized cannot be byte-compared against its own evidence later, and a
 * value that was never bounded is a storage problem before it is a rendering one.
 * ============================================================================================================== */

/** Values ≤ 500 chars; the one multi-line statement field ≤ 2,000. Anything longer is a typed 413, not a truncation. */
export const VALUE_MAX = 500;
export const STATEMENT_MAX = 2000;
export const STATEMENT_FIELDS: ReadonlySet<string> = new Set(['responsibility_statement']);

/** The bound that applies to one field — the same number the server's 413 reports. */
export function maxLengthFor(field: string): number {
  return STATEMENT_FIELDS.has(field) ? STATEMENT_MAX : VALUE_MAX;
}

/**
 * C0/C1 controls. U+000A (`\n`) is deliberately NOT a member: newlines are governed per-field by the newline
 * contract in `inertString` below, not by a blanket rejection. U+000D IS a member, and is normalized away to LF
 * before this test ever runs — a carriage return is a line-ending artefact, never a character somebody typed.
 */
const CONTROL_CHARS = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F]/;
const ZERO_WIDTH_AND_BIDI = /[\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g;

export type InertResult = { ok: true; value: string } | { ok: false; reason: 'too_long' | 'control_chars' };

/**
 * §1.5's INERT-INPUT BOUNDARY, with the NEWLINE CONTRACT stated rather than left to fall out of a regex.
 *
 * THE DIVERGENCE THIS CLOSES (R5.3 round 2, build-review ambiguity ruling 6). The previous shape computed
 * `withoutNewlines` only to run the control-character test over it, and then returned `stripped` — which had
 * newlines in it either way. So `\n` survived in EVERY field, including the single-line ones, because U+000A is
 * not a member of `CONTROL_CHARS`. A `role_title` could be stored containing a line break, and the surface then
 * opened it in a single-line `<input>`, which silently shows the applicant a different string than the one
 * stored: two halves of one contract disagreeing, inside the module whose whole job is that they agree.
 *
 * THE CONTRACT, NOW EXPLICIT AND SYMMETRIC ON BOTH SIDES OF THE WIRE:
 *
 *   * CR and CRLF are normalized to LF FIRST, everywhere. A line ending is a transport artefact; its three
 *     spellings are the same intent and must not produce three different stored byte sequences (or three
 *     different lengths against the bound).
 *   * A MULTI-LINE field (`STATEMENT_FIELDS` — `responsibility_statement`) **PRESERVES** `\n`. It is the §2.2
 *     verbatim clause describing what the applicant did, it is edited in a `<textarea>`, and its line structure
 *     is part of what they wrote.
 *   * A SINGLE-LINE field **STRIPS** `\n`. Stripping, not replacing with a space: substituting a character the
 *     applicant did not type would put a system-authored byte inside a value stored as `applicant_verbatim` and
 *     compared byte-for-byte against its own evidence (T-VERB-1). The value is one line by contract, so the line
 *     break is removed and nothing takes its place.
 *
 * Both sides of the wire run THIS function, so what the field shows, what is sent, and what is stored are one
 * string — which is the entire reason this module is shared rather than reimplemented on the client.
 */
export function inertString(field: string, raw: string): InertResult {
  // NFC FIRST, so the length bound and every later byte-comparison are over the same normalization the surface
  // will render and T-VERB-1 will compare against. Bounding a pre-normalization string would bound a different
  // string than the one actually stored.
  const nfc = raw.normalize('NFC');
  const multiline = STATEMENT_FIELDS.has(field);
  // ONE SPELLING OF A LINE ENDING, before anything else looks at one.
  const lf = nfc.replace(/\r\n?/g, '\n');
  // THE NEWLINE RULE, applied to the VALUE THAT IS RETURNED and not merely to a scratch copy used for testing —
  // which is precisely the bug this closes.
  const newlined = multiline ? lf : lf.replace(/\n/g, '');
  if (CONTROL_CHARS.test(newlined)) return { ok: false, reason: 'control_chars' };
  // Zero-width and bidi controls are STRIPPED rather than rejected: they are invisible by definition, so rejecting
  // them would refuse a paste the user cannot see anything wrong with, while leaving them in would let invisible
  // characters ride into rendered output and into evidence comparisons.
  const stripped = newlined.replace(ZERO_WIDTH_AND_BIDI, '');
  const max = multiline ? STATEMENT_MAX : VALUE_MAX;
  if (stripped.length > max) return { ok: false, reason: 'too_long' };
  return { ok: true, value: stripped };
}
